import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Sort, ArrowDownward, ArrowUpward, Close, AddCircle, FilterAlt } from '@mui/icons-material';
import * as st from '../../core/st';
import { GROUPED_TYPES, BREED_MOUNTS, TYPE_FILTER_ORDER, FILTER_TYPES, CAT } from '../../core/buildUtils';
import './SearchPanel.css';
import NumberInput from '../utils/NumberInput';
import TypeFilterGroup from '../utils/TypeFilterGroup';
import WeaponTypeFilter from '../utils/WeaponTypeFilter';
import Tooltip from '../utils/Tooltip';
import DropdownMenu from '../utils/DropdownMenu';
import ButtonGroup from '../utils/ButtonGroup';
import MediaImg from '../utils/MediaImg';
import WeaponOptions from './WeaponOptions';
import { WEAPON_SORT_DAMAGE } from '../../core/weaponDamage';
import { peekItemCatalog } from '../../services/itemCatalog';

const TXT_SEARCH_MODE = { fr: 'Mode de recherche', en: 'Search mode', de: 'Suchmodus', es: 'Modo de búsqueda', pt: 'Modo de busca' };

const DEFAULT_LEVEL_MIN = 0;
const DEFAULT_LEVEL_MAX = 200;
const LEVEL_MIN_VALUE = 0;
const LEVEL_MAX_VALUE = 200;

const GROUPED_STATS = st.STATS.reduce((groups, stat) => {
    const groupKey = stat.category;
    if (groupKey === undefined || stat.id === st.LVL_ID) return groups;
    if (!groups[groupKey]) groups[groupKey] = [];
    groups[groupKey].push(stat);
    return groups;
}, {});
const QUICK_FILTER_CONFIG = st.STAT_FILTER_CATEGORIES || {};
const QUICK_FILTER_MERGE_MAP = QUICK_FILTER_CONFIG.mergeMap || {};
const QUICK_FILTER_EXTRA_STATS = QUICK_FILTER_CONFIG.extraStats || {};
const QUICK_FILTER_CATEGORY_ORDER = [
    ...(QUICK_FILTER_CONFIG.left || []),
    ...(QUICK_FILTER_CONFIG.right || []),
];

// Typing debounce. 500ms is a *network* budget: it exists so a burst of
// keystrokes costs one request instead of one per character. Search now answers
// from the local catalog in ~11ms — items, sets and both together — so that
// budget buys nothing once the catalog has loaded, and is simply 500ms of the
// search feeling slow. It still applies before then, when a search is a request
// again.
//
// The short window is not zero: each search re-renders a grid of item cards, and
// ~20ms still coalesces a fast typist's burst into one pass while staying far
// below the ~100ms that reads as instant.
const DEBOUNCE_TIME = 500;
const LOCAL_DEBOUNCE_TIME = 20;
const TOGGLE_DEBOUNCE_TIME = 10;
const DEFAULT_STAT_MIN_VALUE = 1;
const SEARCH_MODE_ALL = 'all';
const SEARCH_MODE_ITEMS = 'items';
const SEARCH_MODE_SETS = 'sets';
// Neither is a filter. The first is whether the cards work out what each weapon
// does on the build's sheet or just print its dice; the second is what the
// results are ordered by, which the search resolves into a scorer of its own.
const WEAPON_DAMAGE_DEFAULT = true;
const WEAPON_SORT_DEFAULT = WEAPON_SORT_DAMAGE;
const TXT_STATS_LABEL = { fr: 'Stats', en: 'Stats', de: 'Stats', es: 'Stats', pt: 'Stats' };
const TXT_TYPES_LABEL = { fr: 'Filtres', en: 'Filters', de: 'Filter', es: 'Filtros', pt: 'Filtros' };
const TXT_SEARCH_MODE_ALL = { fr: 'Tout', en: 'All', de: 'Alle', es: 'Todo', pt: 'Tudo' };
const TXT_SEARCH_MODE_ITEMS = { fr: 'Items', en: 'Items', de: 'Gegenstände', es: 'Objetos', pt: 'Itens' };
const TXT_SEARCH_MODE_SETS = { fr: 'Panoplies', en: 'Sets', de: 'Sets', es: 'Sets', pt: 'Conjuntos' };
const TXT_REMOVE_STAT_FILTER = { fr: 'Retirer le filtre', en: 'Remove filter', de: 'Filter entfernen', es: 'Quitar el filtro', pt: 'Remover o filtro' };
const TXT_SORT_DESC = { fr: 'Trier par ordre décroissant', en: 'Sort descending', de: 'Absteigend sortieren', es: 'Ordenar de forma descendente', pt: 'Ordenar em ordem decrescente' };
const TXT_SORT_ASC = { fr: 'Trier par ordre croissant', en: 'Sort ascending', de: 'Aufsteigend sortieren', es: 'Ordenar de forma ascendente', pt: 'Ordenar em ordem crescente' };
const TXT_SEARCH_PLACEHOLDER = { fr: 'Rechercher...', en: 'Search...', de: 'Suchen...', es: 'Buscar...', pt: 'Buscar...' };
const TXT_LVL = { fr: 'Niv.', en: 'Lvl', de: 'Stufe', es: 'Niv.', pt: 'Nív.' };
const TXT_LVL_MIN = { fr: 'Niv. min', en: 'Min Lvl', de: 'Min. Stufe', es: 'Niv. mín', pt: 'Nív. mín' };
const TXT_LVL_MAX = { fr: 'Niv. max', en: 'Max Lvl', de: 'Max. Stufe', es: 'Niv. máx', pt: 'Nív. máx' };
const TXT_STAT_VALUE = { fr: 'valeur', en: 'value', de: 'Wert', es: 'valor', pt: 'valor' };

const resolveInitialSearchMode = (initialSearchMode, disableTypeFilters) => {
    if (disableTypeFilters) {
        return SEARCH_MODE_ITEMS;
    }
    if (initialSearchMode) {
        const { searchItems, searchSets } = initialSearchMode;
        if (searchItems && !searchSets) {
            return SEARCH_MODE_ITEMS;
        }
        if (!searchItems && searchSets) {
            return SEARCH_MODE_SETS;
        }
    }
    return SEARCH_MODE_ALL;
};

const SearchPanel = ({ onSearch, lang = 'fr', initialFilters, initialSearchMode, disableTypeFilters = false, enableStatsQuickPanel = false, enableWeaponOptions = false, autoFocus = false, bare = false }) => {
    const defaultItemTypes = useMemo(() => {
        const types = {};
        for (const type of FILTER_TYPES) {
            if (!BREED_MOUNTS.includes(type.id)) {
                types[type.id] = true;
            }
        }
        return types;
    }, []);

    const [name, setName] = useState(initialFilters?.name || '');
    const [levelMin, setLevelMin] = useState(initialFilters?.level_min ?? DEFAULT_LEVEL_MIN);
    const [levelMax, setLevelMax] = useState(initialFilters?.level_max ?? DEFAULT_LEVEL_MAX);
    const [sortOrder, setSortOrder] = useState('desc');
    const [weaponDamage, setWeaponDamage] = useState(WEAPON_DAMAGE_DEFAULT);
    const [weaponSort, setWeaponSort] = useState(WEAPON_SORT_DEFAULT);
    const [itemTypes, setItemTypes] = useState(initialFilters?.item_type_ids ? initialFilters.item_type_ids.reduce((acc, id) => ({ ...acc, [id]: true }), {}) : defaultItemTypes);
    const [stats, setStats] = useState(initialFilters?.stats || {});
    const [activeStats, setActiveStats] = useState(() => {
        const initialActiveStats = new Set();
        if (initialFilters?.stats) {
            Object.keys(initialFilters.stats).forEach(statId => {
                initialActiveStats.add(Number(statId));
            });
        }
        return initialActiveStats;
    });
    const [searchMode, setSearchMode] = useState(() => resolveInitialSearchMode(initialSearchMode, disableTypeFilters));
    // Starts as null, not itemTypes: that makes the mount pass count as "changed" so the
    // first search goes out on the short debounce. The long one is there to coalesce
    // typing, and there is nothing to coalesce before the panel has been touched.
    const prevItemTypesRef = useRef(null);

    const searchModeItems = useMemo(
        () => [
            { value: SEARCH_MODE_ALL, label: TXT_SEARCH_MODE_ALL[lang] || TXT_SEARCH_MODE_ALL.en },
            { value: SEARCH_MODE_ITEMS, label: TXT_SEARCH_MODE_ITEMS[lang] || TXT_SEARCH_MODE_ITEMS.en },
            { value: SEARCH_MODE_SETS, label: TXT_SEARCH_MODE_SETS[lang] || TXT_SEARCH_MODE_SETS.en },
        ],
        [lang]
    );

    // Lock search mode to items only when type filters are disabled
    useEffect(() => {
        if (disableTypeFilters && searchMode !== SEARCH_MODE_ITEMS) {
            setSearchMode(SEARCH_MODE_ITEMS);
        }
    }, [disableTypeFilters, searchMode]);

    useEffect(() => {
        const itemTypesChanged = prevItemTypesRef.current !== itemTypes;
        prevItemTypesRef.current = itemTypes;

        // Every search mode reads the catalog now, so the only question is
        // whether it has loaded. Until it has, searching still costs a request
        // and keeps the network budget.
        const typingDebounce = peekItemCatalog() ? LOCAL_DEBOUNCE_TIME : DEBOUNCE_TIME;
        const debounceTime = itemTypesChanged ? TOGGLE_DEBOUNCE_TIME : typingDebounce;

        const debounceTimeout = setTimeout(() => {
            const activeStats = Object.entries(stats).reduce((acc, [statId, value]) => {
                const statValue = value?.min;
                if (statValue !== undefined && statValue !== null && statValue !== '') {
                    const numValue = Number(statValue);
                    if (Number.isFinite(numValue)) {
                        const numStatId = Number(statId);
                        if (Number.isFinite(numStatId)) {
                            if (numValue < 0) {
                                // For negative values: stat <= value
                                acc[numStatId] = { max: numValue };
                            } else if (numValue === 0) {
                                // For zero: stat == 0
                                acc[numStatId] = { eq: numValue };
                            } else {
                                // For positive values: stat >= value
                                acc[numStatId] = { min: numValue };
                            }
                        }
                    }
                }
                return acc;
            }, {});

            const itemTypeIds = Object.keys(itemTypes).filter(id => itemTypes[id]).map(id => Number(id)).filter(id => Number.isFinite(id));

            const filters = {
                name: name || undefined,
                level_min: Number.isFinite(levelMin) ? Number(levelMin) : undefined,
                level_max: Number.isFinite(levelMax) ? Number(levelMax) : undefined,
                item_type_ids: itemTypeIds,
                stats: Object.keys(activeStats).length > 0 ? activeStats : undefined,
                sort_order: sortOrder,
                weapon_damage: enableWeaponOptions && weaponDamage,
                weapon_sort: enableWeaponOptions ? weaponSort : undefined,
                search_mode: searchMode
            };
            const mode = {
                searchItems: searchMode === SEARCH_MODE_ALL || searchMode === SEARCH_MODE_ITEMS,
                searchSets: searchMode === SEARCH_MODE_ALL || searchMode === SEARCH_MODE_SETS
            };
            onSearch(filters, mode);
        }, debounceTime);

        return () => clearTimeout(debounceTimeout);
    }, [name, levelMin, levelMax, itemTypes, stats, sortOrder, weaponDamage, weaponSort, enableWeaponOptions, searchMode, onSearch]);

    const handleTypeToggle = (typeId) => {
        setItemTypes(prev => ({ ...prev, [typeId]: !prev[typeId] }));
    };

    const handleStatClick = (statId) => {
        // Add stat to active filters
        setActiveStats(prev => {
            const updated = new Set(prev);
            if (!updated.has(statId)) {
                updated.add(statId);
                if (!stats[statId]) {
                    setStats(prevStats => ({ ...prevStats, [statId]: { min: DEFAULT_STAT_MIN_VALUE } }));
                }
            }
            return updated;
        });
    };

    const handleRemoveStat = (statId) => {
        setActiveStats(prev => {
            const updated = new Set(prev);
            updated.delete(statId);
            return updated;
        });
        setStats(prevStats => {
            const updatedStats = { ...prevStats };
            delete updatedStats[statId];
            return updatedStats;
        });
    };

    const handleStatChange = (statId, value) => {
        setStats(prev => ({ ...prev, [statId]: { min: value } }));
    };

    const getCategoryLabel = (categoryKey) => {
        return GROUPED_STATS[categoryKey]?.[0]?.category_name?.[lang] || categoryKey;
    };

    const getQuickFilterStatsForCategory = (categoryKey) => {
        const mergedStats = Object.entries(QUICK_FILTER_MERGE_MAP)
            .filter(([, targetCategory]) => targetCategory === categoryKey)
            .flatMap(([sourceCategory]) => GROUPED_STATS[sourceCategory] || []);
        const extraStats = (QUICK_FILTER_EXTRA_STATS[categoryKey] || [])
            .map((statName) => st.STATS.find((stat) => stat.name === statName))
            .filter(Boolean);
        const directStats = GROUPED_STATS[categoryKey] || [];
        return [...directStats, ...mergedStats, ...extraStats];
    };

    const renderStatDropdownItem = (stat) => {
        const isActive = activeStats.has(stat.id);
        return (
            <Tooltip key={stat.id} content={stat.label[lang]}>
                <div
                    className={`dropdown-menu-item stats-dropdown-item ${isActive ? 'active' : ''}`}
                    onClick={() => !isActive && handleStatClick(stat.id)}
                >
                    <span className="dropdown-menu-icon">
                        <img src={stat.icon} alt={stat.label[lang]} />
                    </span>
                </div>
            </Tooltip>
        );
    };

    const renderQuickStatItem = (stat) => {
        const isActive = activeStats.has(stat.id);
        const handleClick = () => {
            if (isActive) {
                handleRemoveStat(stat.id);
                return;
            }
            handleStatClick(stat.id);
        };
        return (
            <button
                key={stat.id}
                type="button"
                className={`stats-quick-filter-item ${isActive ? 'active' : ''}`}
                onClick={handleClick}
                aria-pressed={isActive}
            >
                <MediaImg
                    src={stat.icon}
                    alt={stat.label[lang]}
                    className="stats-quick-filter-icon"
                    fallback=""
                />
                <span className="stats-quick-filter-label">{stat.label[lang]}</span>
            </button>
        );
    };

    const renderActiveStatFilter = (statId) => {
        const stat = st.STATS.find(s => s.id === statId);
        if (!stat) return null;
        const removeStatHint = TXT_REMOVE_STAT_FILTER[lang] || TXT_REMOVE_STAT_FILTER.en;
        return (
            <div key={statId} className="active-stat-filter">
                <Tooltip content={stat.label[lang]}>
                    <div className="active-stat-filter-content">
                        <div className="active-stat-action-slot">
                            <img src={stat.icon} alt={stat.label[lang]} className="stat-icon" />
                            <Tooltip content={removeStatHint} wrapperClassName="active-stat-remove-tooltip-wrap">
                                <button
                                    type="button"
                                    className="remove-stat-button"
                                    onClick={() => handleRemoveStat(statId)}
                                >
                                    <Close fontSize="small" />
                                </button>
                            </Tooltip>
                        </div>
                        <NumberInput
                            value={stats[statId]?.min ?? DEFAULT_STAT_MIN_VALUE}
                            onValueChange={(value) => handleStatChange(statId, value)}
                            placeholder={TXT_STAT_VALUE[lang] || TXT_STAT_VALUE.en}
                            className="stat-input"
                            min={-9999}
                            displayZero={true}
                        />
                    </div>
                </Tooltip>
            </div>
        );
    };

    const renderActiveStatsFilters = () => {
        return Array.from(activeStats).map(renderActiveStatFilter);
    };

    return (
        <>
            <div className={`search-panel column${bare ? ' search-panel--bare' : ' panel'}${enableStatsQuickPanel ? ' has-quick-stats' : ''}`}>
                <div className="search-inputs row">
                    {!disableTypeFilters && (
                        <ButtonGroup
                            className="search-mode-toggle"
                            items={searchModeItems}
                            value={searchMode}
                            onChange={setSearchMode}
                            ariaLabel={TXT_SEARCH_MODE[lang] || TXT_SEARCH_MODE.en}
                        />
                    )}
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={TXT_SEARCH_PLACEHOLDER[lang] || TXT_SEARCH_PLACEHOLDER.en}
                        className="search-name-input"
                        autoFocus={autoFocus}
                    />
                    <div className="level-inputs-container row">
                        <span className="search-level-label">{TXT_LVL[lang] || TXT_LVL.en}</span>
                        <NumberInput
                            value={levelMin}
                            onValueChange={(value) => {
                                const clampedValue = Math.max(LEVEL_MIN_VALUE, Math.min(LEVEL_MAX_VALUE, value));
                                setLevelMin(clampedValue);
                            }}
                            placeholder={TXT_LVL_MIN[lang] || TXT_LVL_MIN.en}
                            min={LEVEL_MIN_VALUE}
                            max={LEVEL_MAX_VALUE}
                            allowNegative={false}
                        />
                        <span className="search-level-separator">|</span>
                        <NumberInput
                            value={levelMax}
                            onValueChange={(value) => {
                                const clampedValue = Math.max(LEVEL_MIN_VALUE, Math.min(LEVEL_MAX_VALUE, value));
                                setLevelMax(clampedValue);
                            }}
                            placeholder={TXT_LVL_MAX[lang] || TXT_LVL_MAX.en}
                            min={LEVEL_MIN_VALUE}
                            max={LEVEL_MAX_VALUE}
                            allowNegative={false}
                        />
                    </div>
                    <Tooltip
                        content={
                            sortOrder === 'desc'
                                ? (TXT_SORT_DESC[lang] || TXT_SORT_DESC.en)
                                : (TXT_SORT_ASC[lang] || TXT_SORT_ASC.en)
                        }
                    >
                        <button
                            type="button"
                            className="level-sort-button alt"
                            onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                            aria-label={
                                sortOrder === 'desc'
                                    ? (TXT_SORT_DESC[lang] || TXT_SORT_DESC.en)
                                    : (TXT_SORT_ASC[lang] || TXT_SORT_ASC.en)
                            }
                        >
                            <Sort fontSize="small" />
                            {sortOrder === 'desc' ? <ArrowDownward fontSize="small" /> : <ArrowUpward fontSize="small" />}
                        </button>
                    </Tooltip>
                </div>

                {!disableTypeFilters && (
                    <div className="filters-container row">
                        <h6 className="types-filter-label">
                            <FilterAlt className="types-filter-label-icon" />
                            {TXT_TYPES_LABEL[lang] || TXT_TYPES_LABEL.en}
                        </h6>
                        {TYPE_FILTER_ORDER.map(categoryKey => {
                            const types = GROUPED_TYPES[categoryKey];
                            if (!types) return null;
                            // Weapons are eleven types where every other
                            // category is a handful, so they get the dropdown
                            // rather than a row that expands into the filters.
                            if (categoryKey === CAT.WEAPON) {
                                return (
                                    <WeaponTypeFilter
                                        key={categoryKey}
                                        typeFilters={itemTypes}
                                        onToggleType={handleTypeToggle}
                                        lang={lang}
                                    />
                                );
                            }
                            return (
                                <TypeFilterGroup
                                    key={categoryKey}
                                    types={types}
                                    typeFilters={itemTypes}
                                    onToggleTypeFilter={handleTypeToggle}
                                    lang={lang}
                                />
                            );
                        })}
                        <DropdownMenu
                            items={[]}
                            dropdownClassName="stats-filter-dropdown"
                            menuClassName="stats-dropdown-menu"
                            showButtonLabel={true}
                            buttonContent={
                                <>
                                    <span className="dropdown-menu-icon">
                                        <AddCircle fontSize="small" />
                                    </span>
                                    <span className="dropdown-menu-label">{TXT_STATS_LABEL[lang] || TXT_STATS_LABEL.en}</span>
                                </>
                            }
                        >
                            {QUICK_FILTER_CATEGORY_ORDER.map(categoryKey => {
                                const dropdownCategoryStats = getQuickFilterStatsForCategory(categoryKey);
                                if (dropdownCategoryStats.length === 0) return null;
                                return (
                                    <div key={categoryKey} className="stats-dropdown-category">
                                        <h6 className="stats-dropdown-category-title">
                                            {getCategoryLabel(categoryKey)}
                                        </h6>
                                        <div className="stats-dropdown-category-items">
                                            {dropdownCategoryStats.map(renderStatDropdownItem)}
                                        </div>
                                    </div>
                                );
                            })}
                        </DropdownMenu>

                        {activeStats.size > 0 && (
                            <div className="active-stats-filters">
                                {renderActiveStatsFilters()}
                            </div>
                        )}
                    </div>
                )}

                {disableTypeFilters && activeStats.size > 0 && (
                    <div className="active-stats-filters">
                        {renderActiveStatsFilters()}
                    </div>
                )}
            </div>

            {enableStatsQuickPanel && (
                <div className="stats-quick-filter-panel">
                    <div className="stats-quick-filter-categories">
                        {QUICK_FILTER_CATEGORY_ORDER.map(categoryKey => {
                            const quickCategoryStats = getQuickFilterStatsForCategory(categoryKey);
                            if (quickCategoryStats.length === 0) return null;
                            return (
                                <div key={categoryKey} className="stats-quick-filter-category">
                                    <h6 className="stats-quick-filter-category-title">{getCategoryLabel(categoryKey)}</h6>
                                    <div className="stats-quick-filter-list">
                                        {quickCategoryStats.map(renderQuickStatItem)}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Last of the controls, closest to the results: what the cards say
                and how they are ordered is the setting you reach for while
                reading the list, not before opening it. */}
            {enableWeaponOptions && (
                <WeaponOptions
                    lang={lang}
                    damageOnBuild={weaponDamage}
                    onDamageOnBuildChange={setWeaponDamage}
                    sort={weaponSort}
                    onSortChange={setWeaponSort}
                />
            )}
        </>
    );
};

export default SearchPanel;
